import { create } from 'zustand';
import type { BatchDto, CommunicationFrame, MeasurementDto } from '@/features/harness/types';

// Cap on the live frame buffer — purely a rendering/memory concern. Every frame is still
// persisted regardless of this cap (see useHarnessMeasurement's accumulation buffer, which is
// independent of this store).
const MAX_LIVE_FRAMES = 500;

// Selection state is added in later stories — keep the skeleton minimal but named.
interface HarnessState {
  activeBatch: BatchDto | null;
  /**
   * Set the active Batch, resetting Batch-scoped state so it never leaks across Batches.
   * `measurements` seeds the Measurement list — empty for a freshly-started Batch, or the
   * Batch's existing Measurements when resuming one (Story 1.5), so the list/plots reflect
   * prior work without a second fetch.
   */
  setActiveBatch: (batch: BatchDto | null, measurements?: MeasurementDto[]) => void;

  /** Measurements recorded in the active Batch, kept ordered by SequenceNumber. */
  measurements: MeasurementDto[];
  /** Append a newly-recorded Measurement, preserving SequenceNumber order (AC2). */
  appendMeasurement: (measurement: MeasurementDto) => void;
  /**
   * Remove a hard-deleted Measurement from the list, dropping it from the current selection if
   * it was selected (so the overlay/comparison table update). Sibling Measurements and the active
   * Batch are untouched (Story 1.6, AC3).
   */
  removeMeasurement: (id: string) => void;
  /**
   * Write a persisted overall Note (Story 2.1) back onto the in-memory Measurement after a
   * successful PATCH, so the annotation panel's field survives a later re-seed on re-selection
   * instead of reverting to the pre-save value carried in the list. No-op if the id is absent.
   */
  updateMeasurementNote: (id: string, note: string | null) => void;

  /** Live communication frames for local rendering (AC1), capped to bound memory. */
  liveFrames: CommunicationFrame[];
  pushFrame: (frame: CommunicationFrame) => void;
  /**
   * Retag every buffered frame carrying `fromId` (a locally-generated pending id) once the
   * Measurement it belongs to resolves to a real server id, or to null if the run failed.
   */
  retagFrames: (fromId: string, toId: string | null) => void;

  /** True while a Measurement run is in flight — drives the in-progress indicator (AC2). */
  inProgressMeasurement: boolean;
  setInProgressMeasurement: (inProgress: boolean) => void;

  /**
   * The selected Measurement ids, driving the overlay plot and comparison table (Story 2.1).
   * Kept ordered by SequenceNumber (Batch/run order) regardless of the order rows are clicked,
   * so the overlay and table always read in run order (AC1/AC2). N=1 is the Story 1.4 single view.
   */
  selectedMeasurementIds: string[];
  /**
   * Add or remove a Measurement from the selection (toggle). The result is re-derived from the
   * SequenceNumber-ordered `measurements` list, so the selection stays in run order. Also
   * recomputes `isLiveTail` (Story 2.2) from the resulting selection.
   */
  toggleMeasurementSelection: (id: string) => void;
  /**
   * Bulk "Select all": select every recorded Measurement, kept in SequenceNumber order. Exits
   * live mode unless the batch happens to hold exactly one Measurement (which is then the tail).
   */
  selectAllMeasurements: () => void;
  /** Bulk "Clear": empty the selection and exit live mode. */
  clearMeasurementSelection: () => void;

  /**
   * True only while the Selection is exactly the single most-recently-completed Measurement (the
   * "tail") — Story 2.2. While true, `appendMeasurement` auto-joins each newly-completed
   * Measurement to the Selection so a live comparison keeps growing without re-selecting (AC1).
   * Any other resulting Selection — an older Measurement, more than one Measurement, or an empty
   * Selection — exits live mode (AC2); re-selecting back down to just the tail re-enters it.
   */
  isLiveTail: boolean;
}

/** The id of the most-recently-completed Measurement (highest SequenceNumber), if any. */
function tailMeasurementId(measurements: MeasurementDto[]): string | undefined {
  return measurements.length > 0 ? measurements[measurements.length - 1].id : undefined;
}

export const useHarnessStore = create<HarnessState>()((set) => ({
  activeBatch: null,
  setActiveBatch: (batch, measurements = []) => {
    // Starting/clearing/resuming a Batch resets Batch-scoped state so it never leaks across
    // Batches — `measurements` is the one piece of prior state a resume legitimately restores.
    // Keep them in SequenceNumber (run) order so the seeded selection reads in run order too.
    const ordered = [...measurements].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    // Default the Selection to every seeded Measurement so resuming a Batch immediately overlays
    // all prior curves (a freshly-started Batch seeds none, so this is an empty Selection). Live
    // mode only when the Selection is exactly the single tail.
    const selectedMeasurementIds = ordered.map((m) => m.id);
    const isLiveTail =
      selectedMeasurementIds.length === 1 &&
      selectedMeasurementIds[0] === tailMeasurementId(ordered);
    set({
      activeBatch: batch,
      measurements: ordered,
      liveFrames: [],
      inProgressMeasurement: false,
      selectedMeasurementIds,
      isLiveTail,
    });
  },

  measurements: [],
  appendMeasurement: (measurement) =>
    set((state) => {
      // Guard against a stale Measurement from a Batch that's no longer active — the
      // device call this came from can outlive the active Batch (cleared or switched
      // while a measurement was running).
      if (state.activeBatch?.id !== measurement.batchId) return state;
      const nextMeasurements = [...state.measurements, measurement].sort(
        (a, b) => a.sequenceNumber - b.sequenceNumber,
      );
      if (!state.isLiveTail) {
        return { measurements: nextMeasurements };
      }
      // Live-tail (Story 2.2): the newly-completed Measurement joins the Selection automatically.
      // Re-deriving from `nextMeasurements` (rather than pushing to the end) keeps the same
      // SequenceNumber-order guarantee `toggleMeasurementSelection` provides, and reuses the exact
      // same recompute path as a manual selection change (Story 2.1) — no live-only code path.
      const nextSelectedIds = new Set([...state.selectedMeasurementIds, measurement.id]);
      return {
        measurements: nextMeasurements,
        selectedMeasurementIds: nextMeasurements
          .filter((m) => nextSelectedIds.has(m.id))
          .map((m) => m.id),
      };
    }),
  removeMeasurement: (id) =>
    set((state) => {
      const measurements = state.measurements.filter((m) => m.id !== id);
      const selectedMeasurementIds = state.selectedMeasurementIds.filter((sid) => sid !== id);
      // Reconcile `isLiveTail` (Story 2.2) the same way `toggleMeasurementSelection` does: a
      // deletion is a selection change too. Deleting the selected tail empties (or shrinks) the
      // Selection, which must exit live mode — otherwise `isLiveTail` stays stale-true and the
      // next completed Measurement would silently auto-select itself into an empty Selection
      // (AC2 violation). Live only when the surviving Selection is exactly the new tail.
      const isLiveTail =
        selectedMeasurementIds.length === 1 &&
        selectedMeasurementIds[0] === tailMeasurementId(measurements);
      return { measurements, selectedMeasurementIds, isLiveTail };
    }),
  updateMeasurementNote: (id, note) =>
    set((state) => ({
      measurements: state.measurements.map((m) => (m.id === id ? { ...m, note } : m)),
    })),

  liveFrames: [],
  pushFrame: (frame) =>
    set((state) => ({ liveFrames: [...state.liveFrames, frame].slice(-MAX_LIVE_FRAMES) })),
  retagFrames: (fromId, toId) =>
    set((state) => ({
      liveFrames: state.liveFrames.map((f) =>
        f.measurementId === fromId ? { ...f, measurementId: toId } : f,
      ),
    })),

  inProgressMeasurement: false,
  setInProgressMeasurement: (inProgress) => set({ inProgressMeasurement: inProgress }),

  selectedMeasurementIds: [],
  toggleMeasurementSelection: (id) =>
    set((state) => {
      const isSelected = state.selectedMeasurementIds.includes(id);
      const nextIds = new Set(state.selectedMeasurementIds);
      if (isSelected) nextIds.delete(id);
      else nextIds.add(id);
      // Re-derive the selection from the SequenceNumber-ordered `measurements` list so it always
      // reads in run order regardless of click order (AC1/AC2). A selected id not present in
      // `measurements` (e.g. just removed) naturally drops out here.
      const nextSelectedMeasurementIds = state.measurements
        .filter((m) => nextIds.has(m.id))
        .map((m) => m.id);
      // Live-tail (Story 2.2): live only when the resulting Selection is exactly the single
      // most-recently-completed Measurement. Anything else — an older Measurement, more than one
      // Measurement, or de-selecting down to empty — exits live mode; re-selecting back down to
      // just the tail re-enters it.
      const isLiveTail =
        nextSelectedMeasurementIds.length === 1 &&
        nextSelectedMeasurementIds[0] === tailMeasurementId(state.measurements);
      return { selectedMeasurementIds: nextSelectedMeasurementIds, isLiveTail };
    }),
  selectAllMeasurements: () =>
    set((state) => {
      const selectedMeasurementIds = state.measurements.map((m) => m.id);
      // Same live-tail rule as a manual selection: live only when the resulting Selection is
      // exactly the single most-recently-completed Measurement.
      const isLiveTail =
        selectedMeasurementIds.length === 1 &&
        selectedMeasurementIds[0] === tailMeasurementId(state.measurements);
      return { selectedMeasurementIds, isLiveTail };
    }),
  clearMeasurementSelection: () => set({ selectedMeasurementIds: [], isLiveTail: false }),

  isLiveTail: false,
}));
